// 🔧 vendors_settings.js
// ✅ Purpose: Admin/Manager Vendor Settings page
// ✅ Features:
// - Manage vendor categories for an event
// - Add, rename, reorder, and remove vendor categories
// - Prevent deletion/rename if category is currently used by a vendor
// - Saves vendor categories and public vendor note to /events/{eventId}/settings/voting
// - Uses standard Let's Go Vote responsive manager layout
// - Shows success/error/warning notifications at the top of the page
// - Auto-clears notifications after 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const VendorsSettings = () => {
  const { eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [usedVendorCategories, setUsedVendorCategories] = useState([]);
  const [vendorsNote, setVendorsNote] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [saving, setSaving] = useState(false);

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    loadSettingsAndUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadSettingsAndUsage = async () => {
    if (!eventId) return;

    try {
      setSettingsLoading(true);

      logger.log("🛍 Loading vendor settings for event:", {
        eventId,
      });

      const settingsRef = doc(
        db,
        "events",
        eventId,
        "settings",
        "voting"
      );

      const settingsSnap = await getDoc(settingsRef);
      const data = settingsSnap.exists() ? settingsSnap.data() : {};

      setSettings(data);
      setVendorCategories(data.vendorCategories || []);
      setVendorsNote(data.vendorsNote || "");

      const used = new Set();

      const vendorsSnap = await getDocs(
        collection(db, "events", eventId, "vendors")
      );

      vendorsSnap.forEach((docSnap) => {
        const category = docSnap.data()?.category;

        if (category) {
          used.add(category);
        }
      });

      setUsedVendorCategories([...used]);
    } catch (err) {
      logger.error("❌ Failed to load vendor settings:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "❌ Failed to load vendor settings.",
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!eventId) return;

    setSaving(true);

    try {
      const normalizedVendorCategories = vendorCategories.map((category) => ({
        name: String(category.name || "").trim(),
        paymentRequired: category.paymentRequired === true,
        amountCents:
          category.paymentRequired === true
            ? Math.round(Number(category.amountCents || 0))
            : 0,
      }));

      const update = {
        vendorCategories: normalizedVendorCategories,
        vendorsNote,
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setVendorCategories(normalizedVendorCategories);

      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Vendor settings saved successfully.",
      });
    } catch (err) {
      logger.error("❌ Failed to save vendor settings:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save vendor settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    const name = newCategoryName.trim();

    if (!name) return;

    const exists = vendorCategories.some(
      (category) =>
        String(category.name || "")
          .toLowerCase()
          .trim() === name.toLowerCase()
    );

    if (exists) {
      showStatus(setStatus, {
        type: "warning",
        message: "That vendor category already exists.",
      });

      return;
    }

    setVendorCategories([
      ...vendorCategories,
      {
        name,
        paymentRequired: false,
        amountCents: 0,
      },
    ]);

    setNewCategoryName("");
  };

  const moveCategory = (index, direction) => {
    const newCategories = [...vendorCategories];

    const swapIndex = direction === "up"
      ? index - 1
      : index + 1;

    if (swapIndex < 0 || swapIndex >= newCategories.length) return;

    [newCategories[index], newCategories[swapIndex]] = [
      newCategories[swapIndex],
      newCategories[index],
    ];

    setVendorCategories(newCategories);
  };

  const deleteCategory = (index) => {
    setVendorCategories(
      vendorCategories.filter((_, categoryIndex) => categoryIndex !== index)
    );
  };

  const totalVendorCategories = vendorCategories.length;

  const lockedVendorCategories = vendorCategories.filter((category) =>
    usedVendorCategories.includes(category.name)
  ).length;

  const lastSavedText = settings?.lastSaved?.toDate?.()
    ? settings.lastSaved.toDate().toLocaleString()
    : "Never";

  if (!ready || eventLoading || settingsLoading || isManager === null) {
    return <FullPageSpinner message="Loading vendor settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🛍 Vendor Settings</h1>

            <p className="lead text-muted mb-0">
              Manage vendor categories and public vendor directory content.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Vendor Settings"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Vendor Categories</div>
                <div className="fs-3 fw-bold">{totalVendorCategories}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Categories In Use</div>
                <div className="fs-3 fw-bold">{lockedVendorCategories}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Vendor Category Tools</strong>
          </div>

          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-8">
                <label className="form-label">Add New Vendor Category</label>

                <input
                  type="text"
                  className="form-control"
                  value={newCategoryName}
                  placeholder="Food, Parts, Merchandise, Services, Clubs, Artists..."
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      addCategory();
                    }
                  }}
                />

                <div className="form-text">
                  Vendor categories control grouping and display order on public vendor pages.
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <button
                  type="button"
                  className="btn btn-success w-100"
                  onClick={addCategory}
                  disabled={!newCategoryName.trim()}
                >
                  ➕ Add Category
                </button>
              </div>
            </div>
          </div>
        </div>

        {vendorCategories.length === 0 ? (
          <div className="card shadow-sm mb-4">
            <div className="card-body text-center py-5">
              <h4>No Vendor Categories Yet</h4>

              <p className="text-muted mb-4">
                Add categories to organize vendors on the public vendor directory.
              </p>
            </div>
          </div>
        ) : (
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <strong>Vendor Categories</strong>

              <div className="small text-muted">
                Rename, reorder, or remove vendor categories. Categories already used by vendors are locked.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-4">
                {vendorCategories.map((category, index) => {
                  const isUsed = usedVendorCategories.includes(category.name);

                  return (
                    <div key={`${category.name}-${index}`} className="col-12 col-md-6">
                      <div className="card h-100 border shadow-sm">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                            <div>
                              <div className="text-muted small">Display Order</div>
                              <div className="fw-bold">#{index + 1}</div>
                            </div>

                            <span
                              className={`badge ${
                                isUsed ? "bg-warning text-dark" : "bg-primary"
                              }`}
                            >
                              {isUsed ? "In Use" : "Editable"}
                            </span>
                          </div>

                          <label className="form-label small text-muted">
                            Vendor Category Name
                          </label>

                          <input
                            type="text"
                            className="form-control mb-3"
                            value={category.name}
                            disabled={isUsed}
                            onChange={(e) => {
                              const updated = [...vendorCategories];

                              updated[index] = {
                                ...updated[index],
                                name: e.target.value,
                              };

                              setVendorCategories(updated);
                            }}
                            title={
                              isUsed
                                ? "This category is used by a vendor and cannot be renamed"
                                : ""
                            }
                          />

                          <div className="border rounded p-3 bg-light mb-3">
                            <div className="form-check form-switch mb-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`vendor-payment-required-${index}`}
                                checked={category.paymentRequired === true}
                                onChange={(e) => {
                                  const updated = [...vendorCategories];

                                  updated[index] = {
                                    ...updated[index],
                                    paymentRequired: e.target.checked,
                                    amountCents: e.target.checked
                                      ? Number(updated[index].amountCents || 0)
                                      : 0,
                                  };

                                  setVendorCategories(updated);
                                }}
                              />

                              <label
                                className="form-check-label fw-bold"
                                htmlFor={`vendor-payment-required-${index}`}
                              >
                                Require payment for this vendor category
                              </label>
                            </div>

                            <label className="form-label small text-muted">
                              Vendor Fee
                            </label>

                            <div className="input-group">
                              <span className="input-group-text">$</span>

                              <input
                                type="number"
                                className="form-control"
                                min="0"
                                step="0.01"
                                disabled={category.paymentRequired !== true}
                                value={Number(category.amountCents || 0) / 100}
                                onChange={(e) => {
                                  const updated = [...vendorCategories];

                                  updated[index] = {
                                    ...updated[index],
                                    amountCents: Math.round(
                                      Number(e.target.value || 0) * 100
                                    ),
                                  };

                                  setVendorCategories(updated);
                                }}
                              />
                            </div>

                            <div className="form-text">
                              This amount will be used later when sending vendor payment links or public vendor checkout.
                            </div>
                          </div>
                          
                          {isUsed && (
                            <div className="small text-danger mb-3">
                              🔒 This category is used by a vendor and cannot be renamed or deleted.
                            </div>
                          )}

                          <div className="d-flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={index === 0}
                              onClick={() => moveCategory(index, "up")}
                            >
                              ↑ Move Up
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={index === vendorCategories.length - 1}
                              onClick={() => moveCategory(index, "down")}
                            >
                              ↓ Move Down
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={isUsed}
                              onClick={() => deleteCategory(index)}
                            >
                              🗑 Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🛍 Vendor Directory Content</strong>
          </div>

          <div className="card-body">
            <p className="text-muted">
              This content appears on the public Vendors page and helps attendees understand what vendors are available.
            </p>

            <ReactQuill
              theme="snow"
              value={vendorsNote}
              onChange={setVendorsNote}
            />
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save Vendor Settings</strong>

              <div className="small text-muted">
                Last saved: {lastSavedText}
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Vendor Settings"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorsSettings;